import { SectionRole } from "../shared/types/roles";

import { toastLocalizedOperationCatcher, toastSuccessMessage } from "./alerts";
import { getLastSectionIdFromCookie } from "./auth";

import { showDeleteConfirmationModal } from "components/modals/DeleteSectionModal";
import { SectionKind, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { slices, store, storeApi } from "store/index";
import { filterMap } from "utils/collections";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { isTemplateSection } from "utils/publishing";
import { useNavigate } from "utils/routing";
import { ModelType } from "utils/type-utils";

export const SECTION_ROLES = [
  SectionRole.STUDENT,
  SectionRole.SECTION_ASSISTANT,
  SectionRole.INSTRUCTOR,
] as SectionRole[];

export const updateSection = (section: Partial<SectionType> & { id: string }) => {
  return storeApi.sections.partial_update(section);
};

export const deleteSection = (sectionId: string) => {
  storeApi.sections.destroy(sectionId);
};

/** Optimistically mark store sections linked to a template as unlinked (pubsub may not reach home). */
export const optimisticallyUnlinkSectionsFromTemplate = (templateSectionId: string): string[] => {
  const linkedSectionIds = filterMap(
    Object.values(store.getState().sections),
    (section) => section.template_id === templateSectionId,
    (section) => section.id,
  );

  for (const id of linkedSectionIds) {
    store.dispatch(
      slices.sections.actions.patchOne({
        id,
        kind: SectionKind.UNLINKED,
        template_id: null,
      }),
    );
  }

  return linkedSectionIds;
};

const rollbackOptimisticUnlinkFromTemplate = (
  templateSectionId: string,
  linkedSectionIds: string[],
) => {
  for (const id of linkedSectionIds) {
    store.dispatch(
      slices.sections.actions.patchOne({
        id,
        kind: SectionKind.LINKED,
        template_id: templateSectionId,
      }),
    );
  }
};

export const getDefaultSection = <T extends ModelType>(sections: T[]): T => {
  const lastAccessedSectionId = getLastSectionIdFromCookie();
  if (lastAccessedSectionId) {
    const section = sections.find((s) => s.id === lastAccessedSectionId);

    if (section) {
      return section;
    }
  }

  return sections[0];
};

export const useSectionSettingActions = () => {
  const navigate = useNavigate();
  const homeUrl = useHomeUrl();

  const handleSectionDelete = async (
    section: Pick<SectionType, "id" | "title" | "kind">,
    navigateToHome: boolean = true,
  ) => {
    const sectionId = section.id;
    const sectionTitle = section.title;

    const didDelete = await showDeleteConfirmationModal({
      sectionId,
      sectionTitle,
      sectionKind: section.kind,
    });
    if (didDelete) {
      const linkedSectionIds = isTemplateSection(section)
        ? optimisticallyUnlinkSectionsFromTemplate(sectionId)
        : [];

      try {
        await storeApi.sections.destroy(sectionId, { skipToast: true });

        toastSuccessMessage(t("settings.delete_section_success"));

        if (navigateToHome) {
          navigate(homeUrl);
        }
      } catch (error) {
        if (linkedSectionIds.length > 0) {
          rollbackOptimisticUnlinkFromTemplate(sectionId, linkedSectionIds);
        }

        toastLocalizedOperationCatcher("delete_failure", {
          item: t("glossary.section"),
        })(error);
      }
    }
  };

  return {
    handleSectionDelete,
  };
};

export const getSectionResourceRole = (
  role: SectionRole,
): "assistant" | "student" | "instructor" | "recording_agent" =>
  role === SectionRole.SECTION_ASSISTANT ? "assistant" : role;
